import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiEye, FiLock, FiInfo, FiMail, FiExternalLink, FiList } = FiIcons;

const SectionTitle = ({ children, number }) => (
  <h2 className="text-xl sm:text-2xl font-bold text-white mt-12 mb-6 flex items-baseline gap-3 group">
    <span className="text-blue-500 font-mono text-lg sm:text-xl opacity-80 group-hover:opacity-100 transition-opacity">
      {number}.
    </span>
    {children}
  </h2>
);

const SubTitle = ({ children }) => (
  <h3 className="text-lg font-semibold text-gray-200 mt-8 mb-4 flex items-center gap-2">
    <div className="w-1.5 h-1.5 rounded-full bg-blue-500/50" />
    {children}
  </h3>
);

const Paragraph = ({ children }) => (
  <p className="text-gray-400 leading-relaxed mb-4 text-[15px] sm:text-base">
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-3 mb-6 ml-1 sm:ml-4">
    {items.map((item, index) => (
      <li key={index} className="flex items-start gap-3 text-gray-400 text-[15px] sm:text-base">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: item }} />
      </li>
    ))}
  </ul>
);

export default function Privacy() {
  const toc = [
    "Information We Collect", "How We Collect Your Information", "How We Use Your Information",
    "How We Share Your Information", "Cookies and Tracking Technologies", "Data Retention",
    "Security of Your Information", "Your Privacy Rights", "California Privacy Rights (CCPA/CPRA)",
    "Children’s Privacy (COPPA)", "Third-Party Services and Links", "Email Communications and CAN-SPAM Compliance",
    "Do Not Sell or Share My Personal Information", "Changes to This Privacy Policy", "Contact Us"
  ];

  return (
    <div className="relative w-full overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
      <div className="absolute top-[40%] right-0 w-[600px] h-[600px] bg-indigo-900/5 rounded-full blur-[100px] -z-10 pointer-events-none"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
            <SafeIcon icon={FiShield} />
            Privacy Protection
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6">
            Privacy Policy
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-gray-500 text-sm md:text-base">
            <span>Effective Date: June 1, 2025</span>
            <span className="hidden sm:inline">•</span>
            <span>Last Updated: June 1, 2025</span>
          </div>
          <p className="mt-4 text-blue-400/80 font-medium text-sm">
            Applicable to users in the United States of America
          </p>
        </motion.div>

        {/* Content Container */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-[#111827]/80 backdrop-blur-xl border border-gray-800/60 rounded-2xl md:rounded-3xl p-6 sm:p-10 md:p-14 shadow-2xl shadow-black/40"
        >
          
          <div className="prose prose-invert max-w-none">
            <Paragraph>
              Welcome to <strong>ProfitLense.AI</strong>. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how ProfitLense.AI ("Company", "we", "us", or "our") collects, uses, discloses, and safeguards your information when you visit our website at <a href="https://www.profitlense.ai" className="text-blue-400 hover:underline">www.profitlense.ai</a> and use our platform, tools, and services (collectively, the "Service").
            </Paragraph>
            
            <div className="my-8 p-6 bg-blue-900/5 border border-blue-500/10 rounded-xl italic text-gray-400 text-sm sm:text-base">
              Please read this policy carefully. If you disagree with its terms, please discontinue use of our Service immediately. This Privacy Policy is designed to comply with applicable U.S. federal and state privacy laws, including CCPA/CPRA, COPPA, and the CAN-SPAM Act.
            </div>

            {/* Table of Contents */}
            <div className="mb-12 p-6 bg-gray-900/40 border border-gray-800 rounded-xl">
              <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                <SafeIcon icon={FiList} className="text-blue-500" />
                Table of Contents
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                {toc.map((item, idx) => (
                  <div key={idx} className="text-sm text-gray-500 hover:text-blue-400 cursor-pointer transition-colors flex items-center gap-2">
                    <span className="text-blue-900 font-mono">{idx + 1}.</span> {item}
                  </div>
                ))}
              </div>
            </div>

            <SectionTitle number="1">Information We Collect</SectionTitle>
            <Paragraph>We collect information that you provide directly to us, information collected automatically, and information from third-party sources.</Paragraph>
            
            <SubTitle>1.1 Information You Provide Directly</SubTitle>
            <BulletList items={[
              "<strong>Account Registration Data:</strong> Name, email address, username, and password when you create an account.",
              "<strong>Payment Information:</strong> Billing name, address, and payment card details processed securely. We do not store full credit card numbers.",
              "<strong>Profile Information:</strong> Optional details like trading experience level or preferences.",
              "<strong>Communications:</strong> Messages, feedback, support requests, or other communications you send to us."
            ]} />

            <SubTitle>1.2 Information Collected Automatically</SubTitle>
            <BulletList items={[
              "<strong>Device Information:</strong> IP address, device type, operating system, and browser identifiers.",
              "<strong>Usage Data:</strong> Pages viewed, features used, time spent, and navigation patterns.",
              "<strong>Log Data:</strong> Server log files including access times and referring URLs.",
              "<strong>Approximate Location:</strong> Country, state, or city derived from your IP address."
            ]} />

            <SectionTitle number="2">How We Collect Your Information</SectionTitle>
            <BulletList items={[
              "Directly from you when you register or contact us.",
              "Automatically through cookies and tracking pixels when you use our platform.",
              "From third-party integrations like TradingView or payment processors.",
              "From marketing analytics tools like Google Analytics."
            ]} />

            <SectionTitle number="3">How We Use Your Information</SectionTitle>
            <SubTitle>3.1 To Provide and Operate the Service</SubTitle>
            <Paragraph>Manage accounts, process payments, and deliver access to AI indicators and TradingView integrations.</Paragraph>
            
            <SubTitle>3.2 To Improve and Develop the Service</SubTitle>
            <Paragraph>Analyze usage trends, identify bugs, and develop new analytical features.</Paragraph>

            <SectionTitle number="4">How We Share Your Information</SectionTitle>
            <Paragraph>We do not sell your personal information. We share it only with:</Paragraph>
            <BulletList items={[
              "<strong>Service Providers:</strong> For billing, hosting, email delivery, and analytics.",
              "<strong>Business Transfers:</strong> In case of a merger or acquisition.",
              "<strong>Legal Compliance:</strong> If required by law or court order."
            ]} />

            <SectionTitle number="5">Cookies and Tracking Technologies</SectionTitle>
            <Paragraph>We use cookies to enhance your experience and analyze platform performance. You can manage your preferences through your browser settings, though some features may be affected.</Paragraph>

            <SectionTitle number="6">Data Retention</SectionTitle>
            <Paragraph>We retain account data for the duration of your active account and up to 2 years after closure. Transaction records are held for 7 years for tax compliance.</Paragraph>

            <SectionTitle number="7">Security of Your Information</SectionTitle>
            <div className="p-6 bg-blue-900/5 border border-blue-500/20 rounded-xl mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <SafeIcon icon={FiLock} className="text-blue-400" />
                </div>
                <span className="text-white font-bold">Industry-Standard Protection</span>
              </div>
              <BulletList items={[
                "SSL/TLS encryption for all data transmissions.",
                "PCI-DSS compliance for all payment processing.",
                "Access controls limiting employee data access.",
                "Regular security assessments and vulnerability monitoring."
              ]} />
            </div>

            <SectionTitle number="8">Your Privacy Rights</SectionTitle>
            <Paragraph>Depending on your state, you may have the right to access, correct, delete, or port your data. Contact us at <span className="text-blue-400">privacy@profitlense.ai</span> to exercise these rights.</Paragraph>

            <SectionTitle number="9">California Privacy Rights (CCPA/CPRA)</SectionTitle>
            <Paragraph>California residents have specific rights to know, delete, and correct their data, and to opt-out of the sharing of personal information for cross-context behavioral advertising.</Paragraph>

            <SectionTitle number="10">Children’s Privacy (COPPA)</SectionTitle>
            <Paragraph>ProfitLense.AI is not directed to children under 13. We do not knowingly collect information from children under this age.</Paragraph>

            <SectionTitle number="11">Third-Party Services and Links</SectionTitle>
            <Paragraph>Our service integrates with TradingView, Stripe, and Google Analytics. These parties have their own privacy policies which we encourage you to review.</Paragraph>

            <SectionTitle number="12">Email Communications</SectionTitle>
            <Paragraph>Marketing emails include an opt-out mechanism. We honor all unsubscribe requests within 10 business days as per CAN-SPAM regulations.</Paragraph>

            <SectionTitle number="13">Do Not Sell or Share My Data</SectionTitle>
            <Paragraph>To opt-out of data sharing for targeted advertising, email <span className="text-blue-400">privacy@profitlense.ai</span> with the subject "Do Not Sell or Share My Data".</Paragraph>

            <SectionTitle number="14">Changes to This Policy</SectionTitle>
            <Paragraph>We will notify you of material changes via email or a prominent notice on our platform. Continued use constitutes acceptance of the revised policy.</Paragraph>

            <SectionTitle number="15">Contact Us</SectionTitle>
            <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-6 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-white font-semibold mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiShield} className="text-blue-500" />
                  ProfitLense.AI — Privacy Team
                </p>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-400"><span className="text-gray-500 inline-block w-16">Email:</span> privacy@profitlense.ai</p>
                  <p className="text-gray-400"><span className="text-gray-500 inline-block w-16">Support:</span> support@profitlense.ai</p>
                </div>
              </div>
              <div className="flex flex-col justify-end items-start sm:items-end">
                <a href="https://www.profitlense.ai/privacy-request" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-lg transition-colors">
                  Submit Privacy Request
                  <SafeIcon icon={FiExternalLink} />
                </a>
              </div>
            </div>

            <div className="mt-16 pt-8 border-t border-gray-800/60 text-center">
              <p className="text-gray-500 text-sm">
                © 2025 ProfitLense.AI. All Rights Reserved. This Privacy Policy is intended for users in the United States.
              </p>
            </div>

          </div>
        </motion.div>
      </div>
    </div>
  );
}